import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Shield, Settings as SettingsIcon, BarChart3 } from "lucide-react";
import { getConsent, saveConsent, acceptAll, rejectAll } from "@/lib/cookieConsent";

const SKYLINE_URL = "/assets/skyline-baustelle.png";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(false);

  // First-render check + listen to re-open events
  useEffect(() => {
    const consent = getConsent();
    setVisible(!consent);
    const onOpen = () => {
      const c = getConsent();
      if (c) { setFunctional(c.functional); setAnalytics(c.analytics); }
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("qht-open-cookie-settings", onOpen);
    return () => window.removeEventListener("qht-open-cookie-settings", onOpen);
  }, []);

  function handleAcceptAll() {
    acceptAll();
    setVisible(false);
    setShowSettings(false);
  }
  function handleRejectAll() {
    rejectAll();
    setVisible(false);
    setShowSettings(false);
  }
  function handleSaveSettings() {
    saveConsent({ functional, analytics });
    setVisible(false);
    setShowSettings(false);
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] p-4 sm:p-6 pointer-events-none" data-testid="cookie-banner">
      <div className="max-w-3xl mx-auto pointer-events-auto">
        <div className="bg-white border border-brand-line shadow-[0_8px_40px_rgba(0,0,0,0.10)] overflow-hidden">
          {/* Header label (compact, without skyline) */}
          <div className="px-5 sm:px-8 pt-5 pb-2 bg-[hsl(var(--brand-soft))] border-b border-brand-line/60">
            <div className="inline-flex items-center gap-2 text-[hsl(var(--brand-green-dark))]">
              <Cookie className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.18em] font-medium">Cookie-Einstellungen</span>
            </div>
          </div>

          {!showSettings ? (
            <div className="px-5 sm:px-8 pt-5 pb-6">
              <h2 className="font-semibold text-xl sm:text-2xl mb-2">Wir respektieren Ihre Privatsphäre</h2>
              <p className="text-sm text-neutral-600 leading-relaxed mb-5">
                Diese Webseite verwendet Cookies, um die Grundfunktionen bereitzustellen, die Bedienung zu verbessern und — sofern Sie zustimmen — die Nutzung zu analysieren. Notwendige Cookies sind für den Betrieb der Seite erforderlich. Weitere Informationen finden Sie in unserer{" "}
                <Link to="/cookies" className="text-[hsl(var(--brand-green-strong))] underline hover:no-underline">Cookie-Richtlinie</Link>{" "}
                und in den{" "}
                <Link to="/datenschutz" className="text-[hsl(var(--brand-green-strong))] underline hover:no-underline">Datenschutzhinweisen</Link>.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={handleAcceptAll}
                  data-testid="cookie-accept-all"
                  className="flex-1 bg-[hsl(var(--brand-green-dark))] text-white px-5 py-3 text-sm font-medium hover:bg-[hsl(var(--brand-green-strong))] transition-colors"
                >
                  Alle akzeptieren
                </button>
                <button
                  onClick={handleRejectAll}
                  data-testid="cookie-reject"
                  className="flex-1 bg-white border border-brand-line text-neutral-800 px-5 py-3 text-sm font-medium hover:bg-neutral-50 transition-colors"
                >
                  Nur notwendige Cookies
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  data-testid="cookie-open-settings"
                  className="sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-medium text-[hsl(var(--brand-green-strong))] px-5 py-3 hover:underline"
                >
                  <SettingsIcon className="h-4 w-4" /> Einstellungen anpassen
                </button>
              </div>
            </div>
          ) : (
            <div className="px-5 sm:px-8 pt-5 pb-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="font-semibold text-xl sm:text-2xl mb-1">Cookie-Einstellungen</h2>
                  <p className="text-sm text-neutral-500">Wählen Sie aus, welche Cookies Sie zulassen möchten.</p>
                </div>
                <button onClick={() => setShowSettings(false)} aria-label="Schließen" className="text-neutral-400 hover:text-neutral-900">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-3">
                <CategoryRow
                  icon={<Shield className="h-4 w-4 text-[hsl(var(--brand-green-strong))]" />}
                  title="Notwendig"
                  desc="Erforderlich für den Betrieb der Webseite (Sicherheits-Token, Sitzungs-Speicherung, Cookie-Auswahl)."
                  checked={true}
                  disabled={true}
                  badge="Immer aktiv"
                  testid="cookie-cat-necessary"
                />
                <CategoryRow
                  icon={<SettingsIcon className="h-4 w-4 text-[hsl(var(--brand-green-strong))]" />}
                  title="Funktional"
                  desc="Ermöglicht erweiterte Funktionen wie das Laden externer Karten-Tiles (Leaflet/OpenStreetMap) und Video-Inhalte."
                  checked={functional}
                  onChange={setFunctional}
                  testid="cookie-cat-functional"
                />
                <CategoryRow
                  icon={<BarChart3 className="h-4 w-4 text-[hsl(var(--brand-green-strong))]" />}
                  title="Analyse"
                  desc="Anonymisierte Nutzungsstatistiken helfen uns, die Webseite zu verbessern. Aktuell nicht eingesetzt — kann später aktiviert werden."
                  checked={analytics}
                  onChange={setAnalytics}
                  testid="cookie-cat-analytics"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6">
                <button
                  onClick={handleAcceptAll}
                  data-testid="cookie-settings-accept-all"
                  className="flex-1 bg-[hsl(var(--brand-green-dark))] text-white px-5 py-3 text-sm font-medium hover:bg-[hsl(var(--brand-green-strong))] transition-colors"
                >
                  Alle akzeptieren
                </button>
                <button
                  onClick={handleSaveSettings}
                  data-testid="cookie-settings-save"
                  className="flex-1 bg-white border border-brand-line text-neutral-800 px-5 py-3 text-sm font-medium hover:bg-neutral-50 transition-colors"
                >
                  Auswahl speichern
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ icon, title, desc, checked, onChange, disabled, badge, testid }) {
  return (
    <div className="border border-brand-line px-4 py-3 flex items-start gap-4" data-testid={testid}>
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-neutral-900">{title}</p>
        <p className="text-xs text-neutral-500 leading-relaxed mt-0.5">{desc}</p>
      </div>
      <div className="flex items-center gap-2 pt-0.5">
        {badge && <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--brand-green-strong))]">{badge}</span>}
        <label className={`inline-flex items-center cursor-${disabled ? "not-allowed" : "pointer"}`}>
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
            className="sr-only peer"
          />
          <div className={`relative w-10 h-5 ${disabled ? "bg-[hsl(var(--brand-green-strong))]" : "bg-neutral-300 peer-checked:bg-[hsl(var(--brand-green-strong))]"} transition-colors`}>
            <span className={`absolute top-0.5 ${checked ? "left-[1.4rem]" : "left-0.5"} h-4 w-4 bg-white shadow-sm transition-all`} />
          </div>
        </label>
      </div>
    </div>
  );
}
